import { useCallback, useEffect, useRef } from 'react'
import { showApiError } from '@/lib/utils/notifications'
import * as transferApi from '../api/transferApi'
import { companyOptionLabelWithListedStatus } from '../utils/transferCompanySelectLabel'
import type { TransferFragment } from '../types'
import type { TransferProcessCoreState } from './useTransferProcessCoreState'

type LookupsParams = {
  fragment: TransferFragment | null
  entityId: string
  fkCompanyId: string
  core: TransferProcessCoreState
}

export function useTransferProcessLookups({ fragment, entityId, fkCompanyId, core }: LookupsParams) {
  const {
    setProcessing,
    setCompanyList,
    setCompanyOptionsLoading,
    setCompanyListOld,
    setCompanyOldOptionsLoading,
    setPropertyList,
    setAllProperty,
    setPropertyOptionsLoading,
  } = core

  const companySearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const propertySearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadCompany = useCallback(
    async (searchText?: string, opts?: { silent?: boolean }) => {
      if (!fragment) return
      const silent = opts?.silent ?? false
      if (!silent) setProcessing(true)
      setCompanyOptionsLoading(true)
      try {
        const rows = await transferApi.searchAllCompanies(
          searchText,
          fragment === 'transfer_property' && searchText ? 'transfer_data' : undefined,
        )
        setCompanyList(
          rows.map((c) => ({
            value: String(c.company_id),
            label: companyOptionLabelWithListedStatus(
              String(c.company_name ?? ''),
              c.listed,
              fragment,
            ),
          })),
        )
      } catch (e) {
        showApiError(e)
      } finally {
        setCompanyOptionsLoading(false)
        if (!silent) setProcessing(false)
      }
    },
    [fragment, setProcessing, setCompanyOptionsLoading, setCompanyList],
  )

  const debouncedLoadCompanySearch = useCallback(
    (s: string) => {
      if (companySearchDebounceRef.current) clearTimeout(companySearchDebounceRef.current)
      companySearchDebounceRef.current = setTimeout(() => void loadCompany(s), 300)
    },
    [loadCompany],
  )

  const loadCompanyOld = useCallback(
    async (searchText?: string, opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false
      if (!silent) setProcessing(true)
      setCompanyOldOptionsLoading(true)
      try {
        const rows = await transferApi.searchAllCompanies(searchText)
        setCompanyListOld(
          rows.map((c) => ({ value: String(c.company_id), label: String(c.company_name) })),
        )
      } catch (e) {
        showApiError(e)
      } finally {
        setCompanyOldOptionsLoading(false)
        if (!silent) setProcessing(false)
      }
    },
    [setProcessing, setCompanyOldOptionsLoading, setCompanyListOld],
  )

  const loadProperty = useCallback(
    async (searchText?: string, opts?: { silent?: boolean }) => {
      if (!fragment) return
      const silent = opts?.silent ?? false
      if (!silent) setProcessing(true)
      setPropertyOptionsLoading(true)
      try {
        let companyKey = ''
        if (fragment === 'transfer_company') {
          companyKey = entityId
        } else if (fkCompanyId) {
          companyKey = fkCompanyId
        }
        if (!companyKey) {
          setPropertyList([])
          setAllProperty([])
          return
        }
        const rows = await transferApi.searchAllProperties(companyKey, searchText)
        setAllProperty(rows as unknown as Record<string, unknown>[])
        setPropertyList(rows.map((p) => ({ value: String(p.property_id), label: String(p.property_name) })))
      } catch (e) {
        showApiError(e)
      } finally {
        setPropertyOptionsLoading(false)
        if (!silent) setProcessing(false)
      }
    },
    [
      fragment,
      entityId,
      fkCompanyId,
      setProcessing,
      setPropertyOptionsLoading,
      setPropertyList,
      setAllProperty,
    ],
  )

  const searchProperty = useCallback(
    (text: string) => {
      if (propertySearchDebounceRef.current) clearTimeout(propertySearchDebounceRef.current)
      propertySearchDebounceRef.current = setTimeout(() => void loadProperty(text), 300)
    },
    [loadProperty],
  )

  useEffect(() => {
    return () => {
      if (companySearchDebounceRef.current) clearTimeout(companySearchDebounceRef.current)
      if (propertySearchDebounceRef.current) clearTimeout(propertySearchDebounceRef.current)
    }
  }, [])

  return {
    loadCompany,
    debouncedLoadCompanySearch,
    loadCompanyOld,
    loadProperty,
    searchProperty,
  }
}

export type TransferProcessLookups = ReturnType<typeof useTransferProcessLookups>
